import { useState, useEffect } from 'react';
import { Box, Button, Typography } from '@mui/material';
// TODO: replace with svg
import { FaChevronLeft } from 'react-icons/fa';

import { type QtiQuestionDTO, QuestionRenderer } from '@/src/lib';
import { apiRequest } from '@/src/services';

interface PreviewQuestionModalProps {
    questionId: number;
    submittedAnswer: string;
    questionAnswer: string;
    onClose: () => void;
}

export default function PreviewQuestionModal({
    questionId,
    submittedAnswer,
    questionAnswer,
    onClose,
}: PreviewQuestionModalProps) {
    const [questionData, setQuestionData] = useState<QtiQuestionDTO | null>(null);
    const [questionCache, setQuestionCache] = useState<Record<number, QtiQuestionDTO>>({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchQuestion = async () => {
            if (questionCache[questionId]) {
                setQuestionData(questionCache[questionId]);
                return;
            }

            setLoading(true);
            try {
                const res = await apiRequest<QtiQuestionDTO>(
                    'get',
                    `/assessments/questions/${questionId}`
                );
                setQuestionData(res.data);
                setQuestionCache((prev) => ({ ...prev, [questionId]: res.data }));
            } catch (err) {
                console.error('Error fetching question:', err);
                setQuestionData(null);
            } finally {
                setLoading(false);
            }
        };

        fetchQuestion();
    }, [questionId, questionCache]);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                width: '100%',
                borderLeft: '1px solid #ddd',
                overflow: 'hidden', // important: isolate scroll area
            }}
        >
            {/* Sticky Header */}
            <Box
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    bgcolor: 'background.paper',
                    borderBottom: '1px solid #ddd',
                    p: 2.5,
                    flexShrink: 0,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Box>
                        <Typography variant="h6" fontWeight="bold">
                            Question Preview
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Question ID: {questionId}
                        </Typography>
                    </Box>

                    <Button variant="outlined" onClick={onClose}>
                        <FaChevronLeft size={20} />
                    </Button>
                </Box>
            </Box>

            {/* Scrollable Content */}
            <Box
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    p: 3,
                    backgroundColor: 'background.paper',
                    scrollBehavior: 'smooth',
                }}
            >
                {loading && <Typography>Loading question...</Typography>}
                {!loading && questionData && (
                    <>
                        <Box sx={{ mb: 4 }}>
                            <QuestionRenderer
                                question={questionData}
                                loadedUserResponse={submittedAnswer}
                                stimMode="sev"
                                options={{
                                    hideKeypad: true,
                                    disabled: true,
                                    language: 'en',
                                }}
                            />
                            <Typography
                                variant="subtitle1"
                                fontWeight="bold"
                                color="primary"
                                sx={{ mt: 2 }}
                            >
                                Student’s Answer
                            </Typography>
                        </Box>

                        <hr />

                        <Box sx={{ mt: 4 }}>
                            <QuestionRenderer
                                question={questionData}
                                loadedUserResponse={questionAnswer}
                                stimMode="sev"
                                options={{
                                    hideKeypad: true,
                                    disabled: true,
                                    hidePrompt: true,
                                    language: 'en',
                                }}
                            />
                            <Typography
                                variant="subtitle1"
                                fontWeight="bold"
                                color="success.main"
                                sx={{ mt: 2 }}
                            >
                                Correct Answer
                            </Typography>
                        </Box>
                    </>
                )}
                {!loading && !questionData && (
                    <Typography color="error">Unable to load question details.</Typography>
                )}
                <Box sx={{ height: 40 }} /> {/* scroll padding */}
            </Box>
        </Box>
    );
}
